import {
  useModals,
  Modal,
  ModalProps,
  Box,
  Button,
  FontAwesomeIcon,
  Skeleton,
  Stack,
} from "@skailer-com/uikit";
import { ApplicantMergeProfileCompare } from "@/pages/recruit/candidates/components/ApplicantMergeProfileCompare.tsx";
import { useApplicantDetails, useApplicantsMerge } from "@/queries/recruit/use-applicants.query.ts";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import { ApiApplicant, ApiApplicantShort } from "@/types/recruit/applicant.types.ts";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";

export interface ApplicantsMergeModalProps extends ModalProps {
  applicant: ApiApplicant;
  similarApplicants?: ApiApplicantShort[];
}

export function ApplicantsMergeModal({
  applicant,
  similarApplicants: initialSimilarApplicants,
  ...props
}: ApplicantsMergeModalProps) {
  const { close } = useModals();
  const { id } = { ...props };
  const { t } = useTranslation();

  const [similarApplicants, setSimilarApplicants] = useState(
    initialSimilarApplicants ?? applicant.similar_applicants
  );
  const [similarApplicantIndex, setSimilarApplicantIndex] = useState<number>(0);

  const { data: similarApplicant } = useApplicantDetails(
    similarApplicants[similarApplicantIndex].id
  );

  const applicantsMerge = useApplicantsMerge();

  const handleMerge = useCallback(
    async (resolveType: string) => {
      if (!similarApplicant) return;

      try {
        const result = await applicantsMerge.mutateAsync({
          applicantId: applicant.id,
          similarApplicantId: similarApplicant.id,
          resolveType: resolveType,
        });

        if (result) {
          analyticsService.trackEvent(analyticEvents.applicants.mergeResolved, {
            [analyticProperties.id]: applicant.id,
            [analyticProperties.actionType]: resolveType,
          });
          const updatedApplicants = similarApplicants.filter(
            (_, index) => index !== similarApplicantIndex
          );
          setSimilarApplicants(updatedApplicants);

          if (updatedApplicants.length === 0) {
            close(id);
          } else {
            if (similarApplicantIndex >= updatedApplicants.length) {
              setSimilarApplicantIndex(updatedApplicants.length - 1);
            }
          }
        }
      } catch {
        return;
      }
    },
    [applicant, similarApplicant, similarApplicants, similarApplicantIndex, id]
  );

  const handleChangeSimilar = useCallback((newIndex: number) => {
    setSimilarApplicantIndex(newIndex);
  }, []);

  return (
    <>
      <Modal
        {...props}
        title={t("common:comparison")}
        withCloser
        closeByEscEnabled
        layout={"stretch"}
        size={"xl"}
        actions={
          <Stack gap={"sm"}>
            <Button
              isLoading={applicantsMerge.isPending}
              onClick={() => handleMerge("merge")}
              size={"lg"}
              leftIcon={<FontAwesomeIcon icon={"fa-light fa-check"} />}
            >
              {t("common:merge")}
            </Button>
            <Button
              isLoading={applicantsMerge.isPending}
              onClick={() => handleMerge("ignore")}
              size={"lg"}
              leftIcon={<FontAwesomeIcon icon={"fa-light fa-xmark"} />}
            >
              {t("ats:similar_are_different")}
            </Button>
            <Button
              variant={"plain"}
              size={"lg"}
              onClick={() => {
                close(id);
              }}
            >
              {t("common:cancel")}
            </Button>
          </Stack>
        }
      >
        <Stack gap={"md"} direction={"horizontal"}>
          <Box className="basis-1/2 w-1/2">
            <ApplicantMergeProfileCompare applicant={applicant} />
          </Box>
          <Box className="basis-1/2 w-1/2">
            {!similarApplicant && <Skeleton className="w-full h-4 basis-1/2" />}
            {similarApplicant && (
              <ApplicantMergeProfileCompare
                similarApplicants={initialSimilarApplicants ? undefined : similarApplicants}
                similarApplicantIndex={
                  initialSimilarApplicants ? undefined : similarApplicantIndex + 1
                }
                applicant={similarApplicant}
                onChangeSimilarIndex={handleChangeSimilar}
              />
            )}
          </Box>
        </Stack>
      </Modal>
    </>
  );
}
